import React from 'react';
import classNames from 'classnames';
import moment from 'moment';

import { runInAction } from 'mobx';
import { observer } from 'mobx-react';

import { useStyles } from 'ui/base/theme';
import { FormSubmitEvent } from 'ui/base/types';
import { BusyStore } from 'ui/base/button';
import { PasswordStore } from 'ui/base/input';
import { StandardPage } from 'ui/base/page/standard_page';
import { ShieldIcon, ShieldSize } from 'ui/base/shield_icon/shield_icon';

import { LabeledInput, PasswordInput } from 'ui/public/input';
import { ActiveButton, DefaultButton } from 'ui/public/button';
import { ErrorMessage } from 'ui/public/error_message/error_message';

import { GlobalLocaleStore, localeToLanguageCode } from 'services/base/i18n';

import { PlanFirewallBilling } from 'services/accounts/types';
import { PlanFirewallService } from 'services/accounts/plans_service';
import { getPaymentMethodName } from 'services/accounts/logic/payments/payments';

import {
  getPlanFirewallBillingName,
  getPlanFirewallPackageName,
  getPlanFirewallStripeItemLabel,
} from 'services/accounts/logic/plans/plan_firewall';

import { flattenInvoiceItems, hasProrationItems, totalInvoiceItems } from 'services/accounts/logic/invoices/invoices';

import { formatPrice } from 'assets/enums/currencies/currencies';

import {
  getPlanFirewallBillingIcon,
  getPlanFirewallPackageIcon,
  themedPlanFirewallBillingIconStyles,
  themedPlanFirewallPackageIconStyles,
} from 'assets/enums/plans/firewall/plan_firewall';

import { PlanItemFirewallBaseOptionalModalProps } from '../plan_item_firewall';

import ManagePlansMessages from '../../../../manage_plans_messages';
import Messages from './plan_firewall_confirm_modal_messages';

type PlanItemFirewallConfirmModalProps = PlanItemFirewallBaseOptionalModalProps;
export const PlanItemFirewallConfirmModal = observer(({ styles, accounts, pangolin, store, modal, root }: PlanItemFirewallConfirmModalProps) => {
  const now = new Date();

  const packageIcons = useStyles(themedPlanFirewallPackageIconStyles);
  const billingIcons = useStyles(themedPlanFirewallBillingIconStyles);

  const package_type = store.package_store.value!;
  const billing_type = store.billing_store.value!;
  const payment = store.payment_store.value!;

  const preview_is_trialing = store.preview_is_trialing_store.value;
  const preview_trial_until = store.preview_trial_until_store.value;
  const preview_charges = store.preview_charges_store.value;
  const preview_refunds = store.preview_refunds_store.value;
  const preview_notes = store.preview_notes_store.value;

  const [busy] = React.useState(new BusyStore());
  const [passwordStore] = React.useState(new PasswordStore(''));
  const [passwordError, setPasswordError] = React.useState(' ');

  const disabled = passwordStore.value.length === 0;

  const locale = localeToLanguageCode(GlobalLocaleStore.get());
  const format: object = {
    month: 'short',
    year: 'numeric',
    day: 'numeric',
    weekday: 'short',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
  };

  const onSubmit = React.useCallback(async(event: FormSubmitEvent) => {
    event.preventDefault();

    setPasswordError(' ');

    const result = await busy.perform(async() => {
      try {
        modal.freezeModal();
        return await PlanFirewallService.update(
          accounts, pangolin, payment,
          package_type, billing_type,
          passwordStore.value,
        );
      } finally {
        modal.unfreezeModal();
      }
    });

    if (result.success) {
      runInAction(() => {
        // Navigate to status page
        const is_license = billing_type === PlanFirewallBilling.LIFETIME;
        while (modal.history.position > 0) modal.history.goBack();
        modal.history.replace(`${root}/status`);
        modal.history.push(`${root}/status/${is_license ? 'license' : 'subscription'}`);
      });
    } else if (result.status === 402) {
      // Display payment error
      setPasswordError(Messages.errorPaymentFailed());
    } else if (result.status === 403) {
      // Display authorization error
      setPasswordError(Messages.errorAuthFailed());
    } else if (result.status === 423) {
      // Display lock error
      setPasswordError(Messages.errorLockFailed());
    } else {
      // Display generic error
      setPasswordError(Messages.errorGeneric());
    }
  }, [modal, accounts, pangolin, root, busy, payment, package_type, billing_type, passwordStore]);

  const nestNodeUnderList = (child: React.ReactNode, depth: number, d = 0) => {
    return d === depth ? child : <li>{nestNodeUnderList(child, depth, d + 1)}</li>;
  };

  return (
    <div className={classNames(styles.modalContainerLargest, styles.modalContainerPopOut)}>
      <StandardPage>
        <div className={styles.modalHeaderRow}>
          {Messages.name()}
          <button
            className={styles.modalCloseIcon}
            onClick={modal.dismissModal}
            type='button'
          />
        </div>
        <form className={styles.modalContentRow} onSubmit={onSubmit}>
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {Messages.summaryName()}
            </div>
            <div className={styles.section}>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldTextLeft}>
                  {pangolin.nickname}
                  <div className={styles.fieldInfo}>
                    {Messages.summaryFieldPangolin()}
                  </div>
                </div>
                <ShieldIcon
                  className={styles.fieldIcon}
                  colorCode={pangolin.colorCode}
                  size={ShieldSize.SMALL}
                />
              </div>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldTextLeft}>
                  {getPlanFirewallPackageName(package_type)}
                  <div className={styles.fieldInfo}>
                    {Messages.summaryFieldPackage()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Package Icon'
                     src={getPlanFirewallPackageIcon(packageIcons, package_type)} />
              </div>
              <div className={classNames(styles.field, styles.item)}>
                <div className={styles.fieldTextLeft}>
                  {getPlanFirewallBillingName(billing_type)}
                  <div className={styles.fieldInfo}>
                    {Messages.summaryFieldBilling()}
                  </div>
                </div>
                <img className={styles.fieldIcon} alt='Billing Icon'
                     src={getPlanFirewallBillingIcon(billingIcons, billing_type)} />
              </div>
              {billing_type !== PlanFirewallBilling.LIFETIME && preview_is_trialing && preview_trial_until && now < preview_trial_until && (
                <div className={classNames(styles.field, styles.item)}>
                  <div className={styles.fieldTextLeft}>
                    {Messages.valueTrialPeriod(moment(preview_trial_until).from(now, true))}
                    <div className={styles.fieldInfo}>
                      {Messages.summaryFieldTrial()}
                    </div>
                  </div>
                  <img className={styles.fieldIcon} alt='Trial Icon'
                       src={styles.fieldIconImageTrial} />
                </div>
              )}
            </div>
          </div>
          {(preview_refunds && preview_refunds.length > 0) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {Messages.refundsName()}
              </div>
              {preview_refunds.map((refund, ri) => (
                <div key={`refund-${ri}`}>
                  {refund.payment && (
                    <div className={styles.section}>
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldTextLeft}>
                          {getPaymentMethodName(refund.payment)}
                          <div className={styles.fieldInfo}>
                            {Messages.refundsFieldPayment()}
                          </div>
                        </div>
                        <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
                      </div>
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldTextLeft}>
                          {Messages.valueImmediate()}
                          <div className={styles.fieldInfo}>
                            {Messages.refundsFieldDate()}
                          </div>
                        </div>
                        <img className={styles.fieldIcon} src={styles.fieldIconImageStatusWarn} alt='Warning Icon' />
                      </div>
                    </div>
                  )}
                  <div className={styles.section} key={`refund-${ri}-items`}>
                    {flattenInvoiceItems(refund).map((item, rii) => refund && (
                      <div className={classNames(styles.field, styles.item)} key={`refund-${ri}-item-${rii}`}>
                        <div className={styles.fieldCharge}>
                          {nestNodeUnderList(getPlanFirewallStripeItemLabel(item.label, locale), item.depth)}
                        </div>
                        <div className={styles.fieldAmount}>
                          {formatPrice(refund.currency, item.amount, true)}
                        </div>
                      </div>
                    ))}
                    <div className={classNames(styles.field, styles.item)}>
                      <div className={styles.fieldCharge}>
                        <strong>{Messages.refundsFieldTotal()}</strong>
                      </div>
                      <div className={styles.fieldAmount}>
                        {formatPrice(refund.currency, totalInvoiceItems(refund), true)}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          {(preview_charges && preview_charges.length > 0) && (
            <div className={styles.section}>
              <div className={styles.sectionTitle}>
                {Messages.chargesName()}
              </div>
              {preview_charges.map((charge, ci) => (
                <div key={`charge-${ci}`}>
                  {charge.payment && (
                    <div className={styles.section}>
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldTextLeft}>
                          {getPaymentMethodName(charge.payment)}
                          <div className={styles.fieldInfo}>
                            {Messages.chargesFieldPayment()}
                          </div>
                        </div>
                        <img className={styles.fieldIcon} src={styles.fieldIconImagePayment} alt='Payment Icon' />
                      </div>
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldTextLeft}>
                          {!charge.settled_at ? Messages.valueImmediate() : charge.settled_at.toLocaleString(locale, format)}
                          <div className={styles.fieldInfo}>
                            {Messages.chargesFieldDate()}
                          </div>
                        </div>
                        <img className={styles.fieldIcon} src={styles.fieldIconImageStatusGood} alt='Good Icon' />
                      </div>
                    </div>
                  )}
                  <div className={styles.section} key={`charge-${ci}-items`}>
                    {flattenInvoiceItems(charge).map((item, cii) => preview_charges && (
                      <div className={classNames(styles.field, styles.item)} key={`charge-${ci}-item-${cii}`}>
                        <div className={styles.fieldCharge}>
                          {nestNodeUnderList(getPlanFirewallStripeItemLabel(item.label, locale), item.depth)}
                        </div>
                        <div className={styles.fieldAmount}>
                          {formatPrice(charge.currency, item.amount, true)}
                        </div>
                      </div>
                    ))}
                    {hasProrationItems(charge) ? (<>
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldCharge}>
                          <strong>
                            {Messages.chargesFieldTotalImmediate(billing_type && getPlanFirewallBillingName(billing_type))}
                          </strong>
                        </div>
                        <div className={styles.fieldAmount}>
                          {formatPrice(charge.currency, totalInvoiceItems(charge), true)}
                        </div>
                      </div>
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldCharge}>
                          <strong>
                            {Messages.chargesFieldTotalRecurring(billing_type && getPlanFirewallBillingName(billing_type))}
                          </strong>
                        </div>
                        <div className={styles.fieldAmount}>
                          {formatPrice(charge.currency, totalInvoiceItems(charge, i => !i.proration), true)}
                        </div>
                      </div>
                    </>) : (
                      <div className={classNames(styles.field, styles.item)}>
                        <div className={styles.fieldCharge}>
                          <strong>
                            {Messages.chargesFieldTotal(billing_type && getPlanFirewallBillingName(billing_type))}
                          </strong>
                        </div>
                        <div className={styles.fieldAmount}>
                          {formatPrice(charge.currency, totalInvoiceItems(charge, i => !i.proration), true)}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className={styles.section}>
            <div className={styles.sectionTitle}>
              {Messages.informationName()}
            </div>
            <ul className={styles.notes}>
              {(preview_notes && preview_notes.length > 0) && (
                preview_notes.map((note, ni) => (
                  <li className={styles.item} key={`note-${ni}`}>
                    {note}
                  </li>
                ))
              )}
            </ul>
          </div>
          <LabeledInput label={ManagePlansMessages.inputPassword()}>
            <PasswordInput
              store={passwordStore}
              name='password'
              id='password'
            />
          </LabeledInput>
          <ErrorMessage message={passwordError}/>
          <ActiveButton busy={busy} disabled={disabled}>
            {ManagePlansMessages.actionConfirm()}
          </ActiveButton>
          <DefaultButton type='button' disabled={busy.value} onClick={modal.history.goBack}>
            {ManagePlansMessages.actionBack()}
          </DefaultButton>
        </form>
      </StandardPage>
    </div>
  );
});
